import { Response } from '../../../core/types/http';
import foldersApi from '../api/folders';
import { Folders } from '../api/types';
import { IFoldersService } from './types';

const requestsMap: Record<string, any> = {};

/**
 * Cancels the oldest request for the given request name, if we already have
 * the max number of requests
 *
 * @param namespace - the namespace to check the request config under
 * @param requestName - the name of the request to check for
 */
const abortRequestBeforeNext = (namespace: string, requestName: string): void => {
  // If the config for the namespace or request does not exist, do nothing
  if (!requestsMap[namespace] || !requestsMap[namespace][requestName]) {
    return;
  }

  const requestConfig = requestsMap[namespace][requestName];

  if (
    requestConfig.requests.length &&
    requestConfig.requests.length === requestConfig.maxRequests
  ) {
    const oldestRequest = requestConfig.requests.shift(); // Remove it from the array
    oldestRequest.abort();
  }
};

/**
 * Checks if the oldest request should be canceled
 *
 * @param namespace - the namespace to check under
 * @param requestName - the name of the request to check
 */
const addRequest = (namespace: string, requestName: string) => (request: any): void => {
  // If the config for the namespace or request does not exist, do nothing
  if (!requestsMap[namespace] || !requestsMap[namespace][requestName]) {
    return;
  }

  // Abort the oldest if needed
  abortRequestBeforeNext(namespace, requestName);

  // Add our new request to the array
  requestsMap[namespace][requestName].requests.push(request);
};

export class FoldersService implements IFoldersService {
  /**
   * Deletes a file
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param sort - optional sort
   * @returns the api response or error
   */
  async getRootFolders(
    requestNamespace: string,
    sort: { sortKey: string; direction: string } | null = null
  ) {
    return foldersApi.getRootFolders(sort, addRequest(requestNamespace, 'getRootFolders'));
  }

  // FOLDERS

  /**
   * Gets the form for a folder
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param parentId - the ID of the folder
   * @param sort - optional sort
   * @returns the api response or error
   */
  async getChildrenFolders(
    requestNamespace: string,
    parentId: string,
    sort: { sortKey: string; direction: string } | null = null
  ) {
    return foldersApi.getChildrenFolders(
      parentId,
      sort,
      addRequest(requestNamespace, 'getChildrenFolders')
    );
  }

  /**
   * Gets folder details
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param folderId - the ID of the folder
   * @returns the api response or error
   */
  async getFolder(requestNamespace: string, folderId: string) {
    return foldersApi.getFolder(folderId, addRequest(requestNamespace, 'getFolder'));
  }

  /**
   * Gets folder details
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param getFolderByAlias - the alias of the folder
   * @returns the api response or error
   */
  async getFolderByAlias(requestNamespace: string, folderAlias: string): Promise<unknown> {
    return foldersApi.getFolderByAlias(
      folderAlias,
      addRequest(requestNamespace, 'getFolderByAlias')
    );
  }

  /**
   * Gets the details of the file with the selected attributes
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param folderId - the ID of the folder
   * @returns the api response or error
   */
  async getFolderForm(requestNamespace: string, folderId: string) {
    return foldersApi.getFolderForm(folderId, addRequest(requestNamespace, 'getFolderForm'));
  }

  /**
   * Gets all attributes for a folder
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param folderId - the ID of the folder
   * @returns the api response or error
   */
  async getFolderAttributes(requestNamespace: string, folderId: string) {
    return foldersApi.getFolderAttributes(
      folderId,
      addRequest(requestNamespace, 'getFolderAttributes')
    );
  }

  /**
   * Creates a folder
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param folder - the folder creation object
   * @param parentFolder - optional id of the parent in which to create the folder.
   * Will be created under the root folder if unspecified.
   * @returns the api response or error
   */
  async createFolder(
    requestNamespace: string,
    folder: Folders.CreateFolderDto,
    parentFolder: string | null
  ): Response<Folders.Folder> {
    return foldersApi.createFolder(
      folder,
      parentFolder,
      addRequest(requestNamespace, 'createFolder')
    );
  }
}
